import { Modal } from "semantic-ui-react";
import { PrependForm } from "./modals/PrependForm";
import AppendMessage from "./modals/AppendMessage";
import CreateWorkOrder from "./modals/CreateWorkOrder";
import ShelvingList from "./modals/ShelvingList";
import SumColumnForm from "./modals/SumColumn";
import SupplierDataCSVModal from "./modals/SupplierCsvUpload/SupplierCsvUpload";
import ClearSupplierDataModal from "./modals/ClearSupplierData/ClearSupplierData";
import BarcodeGenerator from "../../../utilities/BarcodeGenerator";
import ShelvingLabelGenerator from "../../../utilities/ShelvingLabelGenerator";
import MessageModal from "../../../components/MessageModal";
import { GridApiPremium } from "@mui/x-data-grid-premium";

export interface ModalSwitchState {
  modalName: string;
  selectedRows: any[];
  appendStatus: boolean;
  appendErrors: any[];
  columnNames: any[];
  mode: string;
  // Barcode modal specific
  barCodeType?: string;
  rowData?: any[];
  // Slot label modal specific
  slotLabelType?: string;
  // Message modal specific
  message?: string;
  messageType?: string;
  messageCopy?: boolean;
  // PrependForm specific
  apiRef?: React.RefObject<GridApiPremium>;
  getSelectedRows?: () => any[];
  setSnackbar?: (snackbar: {
    open: boolean;
    message: string;
    severity: "success" | "error";
  }) => void;
}

interface PricetoolModalSwitchProps {
  modalState: ModalSwitchState;
  closeModal: () => void;
}

/**
 * Centralized modal switcher for all Pricetool modals.
 * Handles both the legacy modals (AppendForm, SumColumn, etc.) and
 * the barcode/slot label modals that were previously separate.
 */
export function PricetoolModalSwitch({
  modalState,
  closeModal,
}: PricetoolModalSwitchProps) {
  const {
    modalName,
    selectedRows,
    appendStatus,
    appendErrors,
    columnNames,
    mode,
    barCodeType,
    rowData,
    slotLabelType,
    message,
    messageType,
    messageCopy,
    apiRef,
    getSelectedRows,
    setSnackbar,
  } = modalState;

  // Legacy modals from old ModalSwitch component
  switch (modalName) {
    case "DamageWorkOrder":
      return <CreateWorkOrder rows={selectedRows} closeModal={closeModal} />;

    case "ShelvingList":
      return <ShelvingList closeModal={closeModal} />;

    case "SumColumn":
      return (
        <SumColumnForm
          rows={selectedRows}
          closeModal={closeModal}
          mode={mode}
        />
      );

    case "AppendMessage":
      return (
        <AppendMessage
          rows={selectedRows}
          closeModal={closeModal}
          status={appendStatus}
          errors={appendErrors as any}
        />
      );

    case "PrependForm":
      if (!apiRef || !getSelectedRows) {
        console.error("PrependForm requires apiRef and getSelectedRows");
        return null;
      }
      return (
        <PrependForm
          columnNames={columnNames}
          closeModal={closeModal}
          apiRef={apiRef}
          getSelectedRows={getSelectedRows}
          setSnackbar={setSnackbar}
        />
      );

    case "UploadSupplierData":
      return <SupplierDataCSVModal closeModal={closeModal} />;

    case "ClearSupplierData":
      return <ClearSupplierDataModal closeModal={closeModal} />;

    // Barcode modal
    case "BarcodeModal":
      return (
        <Modal open={true} onClose={closeModal} style={{ textAlign: "center" }}>
          <Modal.Content>
            <BarcodeGenerator
              height={50}
              fontSize={22}
              textMargin={1}
              margin={0}
              barCodeType={barCodeType || "upc"}
              rowData={rowData || []}
            />
          </Modal.Content>
        </Modal>
      );

    // Slot label modal
    case "SlotLabelModal":
      return (
        <Modal open={true} onClose={closeModal} style={{ textAlign: "center" }}>
          <Modal.Content>
            <ShelvingLabelGenerator
              rowData={rowData || []}
              type={slotLabelType || "inventory"}
            />
          </Modal.Content>
        </Modal>
      );

    // Message modal
    case "MessageModal":
      return (
        <MessageModal
          msg={message || ""}
          open={true}
          setOpen={(isOpen: boolean) => {
            if (!isOpen) closeModal();
          }}
          type={messageType || "upc"}
          copy={messageCopy !== undefined ? messageCopy : true}
        />
      );

    default:
      return null;
  }
}
